import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@env/environment';
import { Subject } from 'rxjs';
import * as braze from '@braze/web-sdk';

export interface BrazePushPayload {
  type: 'BRAZE_PUSH_RECEIVED';
  data: Record<string, unknown> | null;
  timestamp: number;
}

/**
 * Braze Web SDK Service
 *
 * Servicio centralizado para la integración de Braze.
 * Maneja inicialización del SDK, identificación de usuarios y eventos.
 */
@Injectable({
  providedIn: 'root',
})
export class BrazeService {
  private isInitialized = false;
  private isBrowser: boolean;
  private pushListenerAttached = false;

  /** Emite cada vez que llega una notificación push desde Braze (para log, servicios, UI, etc.) */
  readonly onPushReceived = new Subject<BrazePushPayload>();

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  initialize(): void {
    if (!this.isBrowser) return;
    if (!environment.braze?.apiKey || !environment.braze?.endpoint) return;
    if (this.isInitialized) return;
    if (typeof braze === 'undefined') return;

    try {
      braze.initialize(environment.braze.apiKey, {
        baseUrl: `https://${environment.braze.endpoint}`,
        allowUserSuppliedJavascript: false,
        enableLogging: false,
        minimumIntervalBetweenTriggerActionsInSeconds: 5,
        manageServiceWorkerExternally: true,
        serviceWorkerLocation: '/braze-sw.js',
      });

      braze.openSession();

      this.attachPushMessageListener();

      if ('serviceWorker' in navigator) {
        (async () => {
          try {
            const existingRegistrations = await navigator.serviceWorker.getRegistrations();
            const brazeSwRegistered = existingRegistrations.some(
              (reg) =>
                reg.active?.scriptURL?.includes('braze-sw.js') ||
                reg.installing?.scriptURL?.includes('braze-sw.js') ||
                reg.waiting?.scriptURL?.includes('braze-sw.js')
            );

            if (!brazeSwRegistered) {
              await navigator.serviceWorker.register('/braze-sw.js', { scope: '/' });
            } else if (Notification.permission === 'granted') {
              setTimeout(async () => {
                try {
                  const readyRegistration = await navigator.serviceWorker.ready;
                  const existingSubscription = await readyRegistration.pushManager.getSubscription();
                  if (!existingSubscription) {
                    braze.requestPushPermission();
                  }
                } catch {
                  // Error silencioso
                }
              }, 1000);
            }
          } catch (swError) {
            console.error('[BrazeService] Error registering service worker:', swError);
          }
        })();
      }

      try {
        braze.isPushSupported();
      } catch {
        // Error silencioso
      }

      setTimeout(async () => {
        try {
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            const brazeSwRegistered = registrations.some(
              (reg) =>
                reg.active?.scriptURL?.includes('braze-sw.js') ||
                reg.installing?.scriptURL?.includes('braze-sw.js') ||
                reg.waiting?.scriptURL?.includes('braze-sw.js')
            );
            if (brazeSwRegistered && Notification.permission === 'granted') {
              try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                if (!subscription) {
                  braze.requestPushPermission();
                }
              } catch {
                // Error silencioso
              }
            }
          }
        } catch {
          // Error silencioso
        }
      }, 2000);

      this.isInitialized = true;
    } catch (error) {
      console.error('[BrazeService] Failed to initialize Braze SDK:', error);
    }
  }

  identifyUser(userId: string, attributes?: Record<string, any>): void {
    if (!this.isBrowser || !this.isInitialized || !userId || typeof braze === 'undefined') return;

    // Log para trazabilidad: distingue vendedor (userId es email) de cliente (userId es código numérico)
    const tipo = userId.includes('@') ? 'Vendedor' : 'Cliente';
    // console.log(`[Braze] Login ${tipo}`, {
    //   userId,
    //   ...(attributes && Object.keys(attributes).length > 0 ? { attributes } : {}),
    // });

    try {
      braze.changeUser(userId);
      if (attributes && Object.keys(attributes).length > 0) {
        const user = braze.getUser();
        if (user) {
          const email = attributes['email'] || '';
          const name = attributes['name'] || '';
          if (email) user.setCustomUserAttribute('email', email);
          if (name) user.setCustomUserAttribute('name', name);
          Object.keys(attributes).forEach((key) => {
            if (key !== 'email' && key !== 'name') {
              user.setCustomUserAttribute(key, attributes[key]);
            }
          });
        }
      }

      setTimeout(async () => {
        try {
          if (this.getPushPermissionStatus() === 'granted') {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
              try {
                braze.requestPushPermission();
              } catch {
                // Error silencioso
              }
            }
          }
        } catch {
          // Error silencioso
        }
      }, 1000);
    } catch (error) {
      console.error('[BrazeService] Failed to identify user:', error);
    }
  }

  logEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.isBrowser || !this.isInitialized || typeof braze === 'undefined') return;
    try {
      properties && Object.keys(properties).length > 0
        ? braze.logCustomEvent(eventName, properties)
        : braze.logCustomEvent(eventName);
    } catch (error) {
      console.error('[BrazeService] Failed to log event:', error);
    }
  }

  async requestPushPermission(): Promise<boolean> {
    if (!this.isBrowser || !this.isInitialized || typeof braze === 'undefined') return false;

    try {
      const currentStatus = this.getPushPermissionStatus();
      if (currentStatus === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (!subscription) {
            braze.requestPushPermission();
            await new Promise((resolve) => setTimeout(resolve, 1500));
          } else {
            braze.requestPushPermission();
          }
        } catch {
          // Error silencioso
        }
        return true;
      }
      if (currentStatus === 'denied') return false;
      braze.requestPushPermission();
      return true;
    } catch (error) {
      console.error('[BrazeService] Failed to request push permission:', error);
      return false;
    }
  }

  getPushPermissionStatus(): string {
    if (!this.isBrowser || typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  }

  getInitialized(): boolean {
    return this.isInitialized;
  }

  private attachPushMessageListener(): void {
    if (!this.isBrowser || this.pushListenerAttached || !('serviceWorker' in navigator)) return;
    this.pushListenerAttached = true;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const payload = event.data as BrazePushPayload;
      if (payload?.type === 'BRAZE_PUSH_RECEIVED') {
        this.onPushReceived.next(payload);
      }
    });
  }
}
